import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';

/**
 * Rapierのデバッグレンダラー
 * 物理演算の可視化
 */
export class RapierDebugRenderer {
  private scene: THREE.Scene;
  private world: RAPIER.World | null = null;
  private line: THREE.LineSegments | null = null;
  private geometry: THREE.BufferGeometry | null = null;
  private material: THREE.LineBasicMaterial | null = null;
  private positionBuffer: Float32Array | null = null;
  private colorBuffer: Float32Array | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  public setWorld(world: RAPIER.World | null): void {
    this.world = world;
    if (!world) {
      this.disposeLine();
    }
  }

  public render(): void {
    if (!this.world) return;

    const data = this.world.debugRender();
    const vertices = data.vertices;
    const colors = data.colors;

    if (vertices.length === 0 || colors.length === 0) {
      this.disposeLine();
      return;
    }

    this.ensureLine();
    if (!this.geometry) return;

    this.updateBuffer('position', vertices, 3);
    this.updateBuffer('color', colors, 4);

    const drawCount = vertices.length / 3;
    this.geometry.setDrawRange(0, drawCount);
    this.geometry.computeBoundingSphere();
  }

  private ensureLine(): void {
    if (this.line) return;

    this.geometry = new THREE.BufferGeometry();
    this.material = new THREE.LineBasicMaterial({
      vertexColors: true,
      toneMapped: false,
      transparent: true,
      opacity: 1.0,
    });
    this.line = new THREE.LineSegments(this.geometry, this.material);
    this.scene.add(this.line);
  }

  private updateBuffer(key: 'position' | 'color', source: Float32Array, itemSize: number): void {
    if (!this.geometry) return;

    const isPosition = key === 'position';
    const currentBuffer = isPosition ? this.positionBuffer : this.colorBuffer;
    let target = currentBuffer;
    if (!target || target.length !== source.length) {
      target = new Float32Array(source.length);
      const attribute = new THREE.BufferAttribute(target, itemSize);
      this.geometry.setAttribute(key, attribute);
      if (isPosition) {
        this.positionBuffer = target;
      } else {
        this.colorBuffer = target;
      }
    }
    target!.set(source);
    const attribute = this.geometry.getAttribute(key) as THREE.BufferAttribute;
    attribute.needsUpdate = true;
  }

  private disposeLine(): void {
    if (this.line) {
      this.scene.remove(this.line);
      this.line = null;
    }
    if (this.geometry) {
      this.geometry.dispose();
      this.geometry = null;
    }
    if (this.material) {
      this.material.dispose();
      this.material = null;
    }
    this.positionBuffer = null;
    this.colorBuffer = null;
  }
}
